import React, { useEffect, useRef } from 'react';

const SOCIALS = [
    { name: 'Instagram', color: '#ffffff', iconUrl: 'https://cdn-icons-png.flaticon.com/512/1384/1384063.png', url: 'https://instagram.com' },
    { name: 'Facebook', color: '#ffffff', iconUrl: 'https://cdn-icons-png.flaticon.com/512/733/733547.png', url: 'https://facebook.com' },
    { name: 'LinkedIn', color: '#ffffff', iconUrl: 'https://cdn-icons-png.flaticon.com/512/1384/1384072.png', url: 'https://linkedin.com' },
    { name: 'YouTube', color: '#ffffff', iconUrl: 'https://cdn-icons-png.flaticon.com/512/1384/1384060.png', url: 'https://youtube.com' },
    { name: 'Twitter', color: '#ffffff', iconUrl: 'https://cdn-icons-png.flaticon.com/512/3256/3256013.png', url: 'https://twitter.com' },
];

const PhysicsSocials2D = ({ count = 80 }) => {
    const containerRef = useRef(null);
    const canvasRef = useRef(null);
    const ballsRef = useRef([]);

    useEffect(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;

        const ctx = canvas.getContext('2d', { alpha: true });

        // Cache for ball textures to optimize rendering
        const ballCache = document.createElement('canvas');
        const bCacheCtx = ballCache.getContext('2d');

        // Responsive radius
        const isMobile = window.innerWidth < 768;
        const r = isMobile ? 20 : 40; // Smaller radius for mobile

        ballCache.width = r * 2.5;
        ballCache.height = r * 2.5;

        const createBallTexture = () => {
            const centerX = ballCache.width / 2;
            const centerY = ballCache.height / 2;
            const gradient = bCacheCtx.createRadialGradient(
                centerX - r * 0.3, centerY - r * 0.3, r * 0.1,
                centerX, centerY, r
            );
            gradient.addColorStop(0, '#ffffff');
            gradient.addColorStop(0.8, '#f0f0f0');
            gradient.addColorStop(1, '#dcdcdc');

            bCacheCtx.clearRect(0, 0, ballCache.width, ballCache.height);
            bCacheCtx.beginPath();
            bCacheCtx.arc(centerX, centerY, r, 0, Math.PI * 2);
            bCacheCtx.fillStyle = gradient;
            bCacheCtx.fill();
            bCacheCtx.strokeStyle = 'rgba(0,0,0,0.05)';
            bCacheCtx.stroke();
        };
        createBallTexture();

        const resize = () => {
            canvas.width = container.offsetWidth;
            canvas.height = container.offsetHeight;
        };
        resize();

        class Ball {
            constructor(x, y, radius, social) {
                this.x = x;
                this.y = y;
                this.radius = radius;
                this.social = social;
                this.vx = (Math.random() - 0.5) * 4;
                this.vy = (Math.random() - 0.5) * 4;
                this.friction = 0.98;
                this.gravity = 0.35;
                this.bounce = 0.6;

                this.image = new Image();
                this.image.crossOrigin = 'anonymous';
                this.image.src = social.iconUrl;
                this.imageLoaded = false;
                this.image.onload = () => { this.imageLoaded = true; };
            }

            draw() {
                // Use cached ball texture
                ctx.drawImage(
                    ballCache,
                    this.x - this.radius * 1.25,
                    this.y - this.radius * 1.25,
                    this.radius * 2.5,
                    this.radius * 2.5
                );

                if (this.imageLoaded) {
                    const iconSize = this.radius * 0.75;
                    ctx.drawImage(
                        this.image,
                        this.x - iconSize / 2,
                        this.y - iconSize / 2,
                        iconSize,
                        iconSize
                    );
                }
            }

            update(mouseX, mouseY) {
                this.vy += this.gravity;

                const dx = this.x - mouseX;
                const dy = this.y - mouseY;
                const distance = Math.sqrt(dx * dx + dy * dy);
                const mouseRange = 100; // Optimal range for pushing

                if (distance < mouseRange) {
                    const angle = Math.atan2(dy, dx);
                    const force = (mouseRange - distance) / mouseRange;

                    // Physical displacement - Push ball out of mouse area immediately
                    const pushX = Math.cos(angle) * (mouseRange - distance);
                    const pushY = Math.sin(angle) * (mouseRange - distance);

                    this.x += pushX;
                    this.y += pushY;

                    // Add snappier velocity for the toss effect
                    this.vx += Math.cos(angle) * force * 12;
                    this.vy += Math.sin(angle) * force * 12;
                }

                this.vx *= this.friction;
                this.vy *= this.friction;
                this.x += this.vx;
                this.y += this.vy;

                if (this.y + this.radius > canvas.height) {
                    this.y = canvas.height - this.radius;
                    this.vy *= -this.bounce;
                    this.vx *= 0.95;
                } else if (this.y - this.radius < 0) {
                    this.y = this.radius;
                    this.vy *= -this.bounce;
                }

                if (this.x - this.radius < 0) {
                    this.x = this.radius;
                    this.vx *= -this.bounce;
                } else if (this.x + this.radius > canvas.width) {
                    this.x = canvas.width - this.radius;
                    this.vx *= -this.bounce;
                }
            }
        }

        let hasFallen = false;

        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && !hasFallen) {
                hasFallen = true;

                // Initialize balls above the canvas to create the falling effect
                const ballRadius = window.innerWidth < 768 ? 20 : 40;
                for (let i = 0; i < count; i++) {
                    const social = SOCIALS[i % SOCIALS.length];
                    const x = Math.random() * (canvas.width - ballRadius * 2) + ballRadius;
                    // Drop balls from different heights above the canvas
                    const y = -Math.random() * 800 - 100;
                    balls.push(new Ball(x, y, ballRadius, social));
                }
            }
        }, { threshold: 0.1 });

        observer.observe(container);

        const balls = [];
        // const ballRadius = 40;

        // Removed immediate initialization loop - now handled by observer above

        let mouseX = -1000;
        let mouseY = -1000;

        const handleMouseMove = (e) => {
            const rect = canvas.getBoundingClientRect();
            mouseX = e.clientX - rect.left;
            mouseY = e.clientY - rect.top;
        };

        const handleClick = (e) => {
            const rect = canvas.getBoundingClientRect();
            const cx = e.clientX - rect.left;
            const cy = e.clientY - rect.top;
            balls.forEach(ball => {
                const d = Math.sqrt((cx - ball.x) ** 2 + (cy - ball.y) ** 2);
                if (d < ball.radius) window.open(ball.social.url, '_blank');
            });
        };

        window.addEventListener('mousemove', handleMouseMove);
        canvas.addEventListener('click', handleClick);
        window.addEventListener('resize', resize);

        const resolveCollisions = () => {
            for (let i = 0; i < balls.length; i++) {
                for (let j = i + 1; j < balls.length; j++) {
                    const b1 = balls[i];
                    const b2 = balls[j];
                    const dx = b2.x - b1.x;
                    const dy = b2.y - b1.y;
                    const dist = Math.sqrt(dx * dx + dy * dy);
                    const minDist = b1.radius + b2.radius;

                    if (dist < minDist) {
                        const angle = Math.atan2(dy, dx);
                        const sin = Math.sin(angle);
                        const cos = Math.cos(angle);

                        const overlap = minDist - dist;
                        b1.x -= (overlap * cos) / 2;
                        b1.y -= (overlap * sin) / 2;
                        b2.x += (overlap * cos) / 2;
                        b2.y += (overlap * sin) / 2;

                        const v1 = (b1.vx * cos + b1.vy * sin);
                        const v2 = (b2.vx * cos + b2.vy * sin);
                        const v1Final = v2 * 0.85;
                        const v2Final = v1 * 0.85;

                        b1.vx += (v1Final - v1) * cos;
                        b1.vy += (v1Final - v1) * sin;
                        b2.vx += (v2Final - v2) * cos;
                        b2.vy += (v2Final - v2) * sin;
                    }
                }
            }
        };

        let animationId;
        const loop = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Stability passes for solid object feel
            for (let k = 0; k < 5; k++) {
                resolveCollisions();
            }

            balls.forEach(ball => {
                ball.update(mouseX, mouseY);
                ball.draw();
            });

            animationId = requestAnimationFrame(loop);
        };
        loop();

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            canvas.removeEventListener('click', handleClick);
            window.removeEventListener('resize', resize);
            cancelAnimationFrame(animationId);
            observer.disconnect();
        };
    }, [count]);

    return (
        <div ref={containerRef} className="absolute inset-0 pointer-events-none z-0">
            <canvas
                ref={canvasRef}
                className="w-full h-full pointer-events-auto cursor-pointer opacity-80"
                style={{ touchAction: 'none' }}
            />
        </div>
    );
};

export default PhysicsSocials2D;
